// src/services/permissions.js
import AuthService from "./auth.js";

/**
 * Servicio centralizado de permisos
 * Maneja toda la lógica de permisos basada en roles del backend
 */
class PermissionsService {
  constructor() {
    this.baseURL = process.env.VUE_APP_API_URL || "https://efibox.efintel.cl";

    // Cache de permisos
    this.currentUserPermissions = null;
    this.tiposUsuarioConPermisos = [];
    this.cacheExpiration = 30 * 60 * 1000; // 30 minutos
    this.lastUpdate = null;
  }

  // ==========================================
  // CARGAR PERMISOS DEL USUARIO ACTUAL
  // ==========================================

  /**
   * Carga los permisos del usuario actual desde el backend
   * Se llama automáticamente después del login
   */
  async cargarPermisosUsuarioActual() {
    try {
      // Limpiar permisos anteriores
      this.limpiarPermisos();

      // Pequeña pausa para asegurar que la limpieza se completó
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Obtener usuario actual
      const currentUser = await AuthService.getCurrentUser();

      if (!currentUser || !currentUser.tipo_usuario) {
        throw new Error("No se pudo obtener información del usuario");
      }

      // Forzar recarga de tipos de usuario (ignorar caché)
      await this.cargarTiposUsuario(true);

      // Encontrar permisos específicos del tipo de usuario actual
      const tipoUsuario = this.tiposUsuarioConPermisos.find((tipo) => tipo.id === currentUser.tipo_usuario);

      if (!tipoUsuario) {
        this.currentUserPermissions = this.obtenerPermisosVacios();
      } else {
        // Crear objeto completamente nuevo (deep copy)
        const nuevoPermiso = {
          tipo_usuario_id: tipoUsuario.id,
          tipo_usuario_codigo: tipoUsuario.codigo,
          tipo_usuario_nombre: tipoUsuario.nombre_tipo,
          permisos: JSON.parse(JSON.stringify(tipoUsuario.permisos)), // Deep copy
          usuario_id: currentUser.id,
          usuario_email: currentUser.email,
          timestamp: Date.now(),
          session_id: `${currentUser.id}_${Date.now()}`,
        };

        this.currentUserPermissions = nuevoPermiso;
      }

      this.lastUpdate = Date.now();

      return this.currentUserPermissions;
    } catch (error) {
      this.currentUserPermissions = this.obtenerPermisosVacios();
      throw error;
    }
  }

  /**
   * Obtiene el ID del usuario actual
   * @returns {number|null} ID del usuario o null si no hay sesión
   */
  getUserId() {
    return this.currentUserPermissions?.usuario_id || null;
  }

  /**
   * Carga todos los tipos de usuario con sus permisos desde el backend
   */
  async cargarTiposUsuario(forzarRecarga = false) {
    // Verificar caché
    if (!forzarRecarga && this.tiposUsuarioConPermisos.length > 0 && this.esCacheValido()) {
      return this.tiposUsuarioConPermisos;
    }

    // Si se fuerza la recarga, limpiar caché primero
    if (forzarRecarga) {
      this.tiposUsuarioConPermisos = [];
    }

    const response = await AuthService.authenticatedRequest("/usuarios/api/tipos_usuario/");

    // Manejar respuesta paginada
    this.tiposUsuarioConPermisos = Array.isArray(response) ? response : response.results || [];

    return this.tiposUsuarioConPermisos;
  }

  // ==========================================
  // VERIFICAR PERMISOS (MÉTODOS PRINCIPALES)
  // ==========================================

  /**
   * Verifica si el usuario tiene un permiso específico
   * @param {string} recurso - Nombre del recurso (ej: 'locales', 'usuarios')
   * @param {string} accion - Acción a verificar (ej: 'crear', 'editar', 'ver')
   * @returns {boolean} - true si tiene el permiso
   */
  can(recurso, accion) {
    if (!this.currentUserPermissions) {
      return false;
    }

    const permisos = this.currentUserPermissions.permisos;

    // Verificar que existe el recurso
    if (!permisos[recurso]) {
      return false;
    }

    // Verificar que existe la acción
    if (!(accion in permisos[recurso])) {
      return false;
    }

    return permisos[recurso][accion] === true;
  }

  /**
   * Verifica si el usuario NO tiene un permiso
   * @param {string} recurso - Nombre del recurso
   * @param {string} accion - Acción a verificar
   * @returns {boolean} - true si NO tiene el permiso
   */
  cannot(recurso, accion) {
    return !this.can(recurso, accion);
  }

  /**
   * Verifica si el usuario tiene TODOS los permisos especificados
   * @param {Array<{recurso: string, accion: string}>} permisos - Array de permisos
   * @returns {boolean} - true si tiene todos los permisos
   */
  canAll(permisos) {
    return permisos.every(({ recurso, accion }) => this.can(recurso, accion));
  }

  /**
   * Verifica si el usuario tiene AL MENOS UNO de los permisos
   * @param {Array<{recurso: string, accion: string}>} permisos - Array de permisos
   * @returns {boolean} - true si tiene al menos uno
   */
  canAny(permisos) {
    return permisos.some(({ recurso, accion }) => this.can(recurso, accion));
  }

  // ==========================================
  // VERIFICAR ROLES ESPECÍFICOS
  // ==========================================

  /**
   * Verifica si el usuario es administrador
   */
  esAdministrador() {
    if (!this.currentUserPermissions) return false;

    const codigosAdmin = ["SUPERADMIN", "ADMIN"];
    return codigosAdmin.includes(this.currentUserPermissions.tipo_usuario_codigo);
  }

  /**
   * Verifica si el usuario es cliente general
   */
  esCliente() {
    if (!this.currentUserPermissions) return false;
    return this.currentUserPermissions.tipo_usuario_codigo === "CLIENTE_GENERAL";
  }

  /**
   * Verifica si el usuario es encargado de local
   */
  esEncargado() {
    if (!this.currentUserPermissions) return false;
    return this.currentUserPermissions.tipo_usuario_codigo === "CLIENTE_SUCURSAL";
  }

  /**
   * Verifica si el usuario tiene un rol específico
   * @param {string} codigo - Código del rol
   */
  tieneRol(codigo) {
    if (!this.currentUserPermissions) return false;
    return this.currentUserPermissions.tipo_usuario_codigo === codigo;
  }

  // ==========================================
  // OBTENER INFORMACIÓN DE PERMISOS
  // ==========================================

  /**
   * Obtiene todos los permisos del usuario actual
   */
  obtenerTodosLosPermisos() {
    return this.currentUserPermissions?.permisos || {};
  }

  /**
   * Obtiene permisos de un recurso específico
   */
  obtenerPermisosDeRecurso(recurso) {
    return this.currentUserPermissions?.permisos[recurso] || {};
  }

  /**
   * Obtiene información del rol actual
   */
  obtenerInfoRol() {
    if (!this.currentUserPermissions) return null;

    return {
      id: this.currentUserPermissions.tipo_usuario_id,
      codigo: this.currentUserPermissions.tipo_usuario_codigo,
      nombre: this.currentUserPermissions.tipo_usuario_nombre,
    };
  }

  // ==========================================
  // LIMPIEZA Y UTILIDADES
  // ==========================================

  /**
   * Limpia los permisos almacenados
   */
  limpiarPermisos() {
    this.currentUserPermissions = null;
    this.tiposUsuarioConPermisos = [];
    this.lastUpdate = null;
  }

  /**
   * Verifica si el caché es válido
   */
  esCacheValido() {
    if (!this.lastUpdate) return false;
    return Date.now() - this.lastUpdate < this.cacheExpiration;
  }

  /**
   * Retorna un objeto de permisos vacío (sin acceso)
   */
  obtenerPermisosVacios() {
    return {
      tipo_usuario_id: null,
      tipo_usuario_codigo: "NINGUNO",
      tipo_usuario_nombre: "Sin permisos",
      permisos: {
        alarmas: { ver: false, gestionar: false },
        locales: { crear: false, editar: false, eliminar: false, ver_todos: false, ver_propios: false },
        empresas: { ver: false, crear: false, editar: false, eliminar: false, ver_todas: false, ver_propias: false },
        usuarios: { crear: false, editar: false, eliminar: false, ver_todos: false, ver_propios: false },
        dispositivos: { ver: false, crear: false, editar: false, eliminar: false },
        historicos: { ver: false },
      },
      usuario_id: null,
      usuario_email: null,
    };
  }
}

// Exportar instancia única
export default new PermissionsService();
